// Admin ops panel for the Signal-Room import (PLAN #13a): kill-switch state, mapped companies
// with last-import ages + inline mapping edit (PATCH writes an audit row server-side), and the
// last 20 ledger runs. Read path: GET /api/admin/signal-room/status.
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Activity, RefreshCw } from "lucide-react";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { FloatingCard } from "@/components/FloatingCard";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

type SrStatus = {
  flags: { importEnabled: boolean; publishEnabled: boolean };
  companies: Array<{
    companyId: string; name: string; clientId: string | null; slug: string | null;
    importEnabled: boolean; lastImportAt: string | null;
  }>;
  runs: Array<{
    id: string; importId: string; status: string; errorClass: string | null; dryRun: boolean;
    clientId: string | null; schemaVersion: number; counts: Record<string, unknown> | null;
    createdAt: string;
  }>;
  nonceCount: number;
  latestSnapshotWeek: string | null;
};

const QUERY_KEY = ["/api/admin/signal-room/status"];

function ageLabel(iso: string | null): string {
  if (!iso) return "—";
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
  if (hours < 1) return "< 1 h";
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

export function SignalRoomAdminPanel() {
  const { toast } = useToast();
  const [clientIdDraft, setClientIdDraft] = useState<Record<string, string>>({});
  const [newCompanyId, setNewCompanyId] = useState("");
  const [newClientId, setNewClientId] = useState("");

  const { data, isLoading, refetch } = useQuery<SrStatus>({
    queryKey: QUERY_KEY,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const patchMapping = useMutation({
    mutationFn: ({ companyId, clientId, importEnabled }: { companyId: string; clientId: string | null; importEnabled?: boolean }) =>
      apiRequest("PATCH", `/api/admin/signal-room/companies/${companyId}/mapping`, { clientId, importEnabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (err: any) => toast({
      title: "Mapping fehlgeschlagen",
      description: err?.data?.error || err?.message,
      variant: "destructive",
    }),
  });

  const flagBadge = (on: boolean, label: string) => (
    <Badge variant="outline" className={`text-xs ${on ? "text-green-600 border-green-300" : "text-red-600 border-red-300"}`}>
      {label}: {on ? "an" : "aus"}
    </Badge>
  );

  return (
    <FloatingCard>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Activity className="h-5 w-5" />
          <span>Signal-Room Import</span>
        </CardTitle>
        <CardDescription className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            {data && flagBadge(data.flags.importEnabled, "Import")}
            {data && flagBadge(data.flags.publishEnabled, "Publish")}
            {data && <span className="text-xs">Nonces: {data.nonceCount}</span>}
            {data?.latestSnapshotWeek && <span className="text-xs">Letzter Markt-Snapshot: {data.latestSnapshotWeek}</span>}
          </span>
          <Button variant="ghost" size="sm" onClick={() => refetch()} data-testid="button-sr-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Lade…</p>
        ) : (
          <>
            <div>
              <h4 className="text-sm font-semibold mb-2">Zugeordnete Unternehmen</h4>
              {data?.companies.length === 0 ? (
                <p className="text-sm text-muted-foreground">Noch keine Zuordnung. Client-ID unten pro Firma eintragen (über die Firmen-ID aus dem Unternehmen-Tab).</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-5 gap-3 text-xs text-muted-foreground font-medium">
                    <span>Firma</span><span>Client-ID</span><span>Letzter Import</span><span>Import erlaubt</span><span></span>
                  </div>
                  {data?.companies.map((c) => (
                    <div key={c.companyId} className="grid grid-cols-5 gap-3 items-center text-sm" data-testid={`row-sr-company-${c.companyId}`}>
                      <span className="truncate">{c.name}</span>
                      <Input
                        className="h-8 text-xs"
                        value={clientIdDraft[c.companyId] ?? c.clientId ?? ""}
                        onChange={(e) => setClientIdDraft((d) => ({ ...d, [c.companyId]: e.target.value }))}
                      />
                      <span className="text-xs text-muted-foreground">{ageLabel(c.lastImportAt)}</span>
                      <Switch
                        checked={c.importEnabled}
                        onCheckedChange={(v) => patchMapping.mutate({ companyId: c.companyId, clientId: c.clientId, importEnabled: v })}
                      />
                      <Button
                        variant="outline" size="sm" className="h-8"
                        disabled={patchMapping.isPending}
                        onClick={() => patchMapping.mutate({
                          companyId: c.companyId,
                          clientId: (clientIdDraft[c.companyId] ?? c.clientId ?? "").trim() || null,
                        })}
                      >
                        Speichern
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">Neue Zuordnung: Firmen-ID</label>
                <Input className="h-8 text-xs" value={newCompanyId} onChange={(e) => setNewCompanyId(e.target.value)} data-testid="input-sr-new-company" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">Signal-Room Client-ID</label>
                <Input className="h-8 text-xs" value={newClientId} onChange={(e) => setNewClientId(e.target.value)} data-testid="input-sr-new-client" />
              </div>
              <Button
                variant="outline" size="sm" className="h-8"
                disabled={!newCompanyId.trim() || !newClientId.trim() || patchMapping.isPending}
                onClick={() => {
                  patchMapping.mutate({ companyId: newCompanyId.trim(), clientId: newClientId.trim() });
                  setNewCompanyId(""); setNewClientId("");
                }}
                data-testid="button-sr-add-mapping"
              >
                Zuordnen
              </Button>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2">Letzte Import-Läufe</h4>
              {(data?.runs.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">Noch keine Läufe.</p>
              ) : (
                <div className="space-y-1">
                  <div className="grid grid-cols-6 gap-3 text-xs text-muted-foreground font-medium">
                    <span>Zeit</span><span>Client</span><span>Status</span><span>Fehlerklasse</span><span>Zähler</span><span>Modus</span>
                  </div>
                  {data?.runs.map((r) => (
                    <div key={r.id} className="grid grid-cols-6 gap-3 text-xs items-center" data-testid={`row-sr-run-${r.importId}`}>
                      <span>{new Date(r.createdAt).toLocaleString("de-DE")}</span>
                      <span className="truncate">{r.clientId ?? "—"}</span>
                      <Badge variant="outline" className={`w-fit text-xs ${r.status === "accepted" ? "text-green-600 border-green-300" : "text-red-600 border-red-300"}`}>
                        {r.status}
                      </Badge>
                      <span>{r.errorClass ?? "—"}</span>
                      <span className="truncate">{r.counts ? Object.entries(r.counts).filter(([, v]) => typeof v === "number" && v).map(([k, v]) => `${k}:${v}`).join(" ") : "—"}</span>
                      <span>{r.dryRun ? "dry-run" : "live"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </FloatingCard>
  );
}
